import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';

/**
 * Browser-history coordinator for REDI's desktop panels
 * (docs/plans/panel-system.md, "Deep links and back" — Phase 4A). A
 * companion (CompanionPanel) or a drill-in (PanelStack) that opts in pushes
 * a history entry when it opens; browser Back then runs the exact close the
 * Escape key already runs for the topmost layer, LIFO. Closing a panel
 * through the UI (Escape, X, back arrow) removes the entry it pushed instead
 * of leaving a dead one behind.
 *
 * URL: every interactive push/replace here leaves `window.location`
 * UNCHANGED — everything REDI needs (which panel, in what order) lives in
 * `history.state` instead. This was the fallback this plan sketched, not
 * the first thing tried: Expo Router's web linking resolves the current
 * route from `window.location` on every `popstate`, including the ones this
 * module causes, and a companion/drill-in open or close is exactly the kind
 * of "search params changed, pathname didn't" edit that resolution has to
 * treat as a no-op re-render rather than a real navigation. Verified in a
 * real browser (see this phase's delivery report) that it does — the
 * Leaflet container survives a push, a Back and a Forward as the same DOM
 * node either way — but changing the URL on every companion/drill-in open
 * bet the map staying mounted on that behavior holding for every future
 * Expo Router upgrade, for no real benefit: none of today's call sites
 * (Assign, People, the post panel, the property drill-in) need a shareable
 * URL for that specific intermediate state. Shareable entry points
 * (`?plan=`, `&assign=`, `?post=`) still work — they're parsed
 * once on map mount (`parsePanelDeepLinkParams` below) and cleared with the
 * same `router.setParams` pattern `useMapFocus.ts` already uses for
 * `focusStructureId`, never through this module.
 *
 * Exported as a factory, not a ready-made singleton, so jest can hand it a
 * fake `history`/`window` (see `__tests__/hooks/usePanelHistory.test.ts`) —
 * the real app only ever touches the one built from `window.history` below.
 */

const STATE_MARK = 'rediPanel';

interface RediPanelState {
  rediPanel: true;
  seq: number;
  key: string;
}

function isRediPanelState(state: unknown): state is RediPanelState {
  return (
    !!state && typeof state === 'object' && (state as Record<string, unknown>)[STATE_MARK] === true
  );
}

interface HistoryLike {
  pushState(data: unknown, title: string, url?: string | null): void;
  replaceState(data: unknown, title: string, url?: string | null): void;
  back(): void;
}

interface PopStateLike {
  state: unknown;
}

interface WindowLike {
  addEventListener(type: 'popstate', listener: (event: PopStateLike) => void): void;
  removeEventListener(type: 'popstate', listener: (event: PopStateLike) => void): void;
}

interface PanelEntry {
  key: string;
  seq: number;
  onPop: () => void;
}

export interface PanelHistoryCoordinator {
  /** Opens a new layer: pushes a history entry and remembers `onPop`, the
   *  raw (state-only) close to run if browser Back/Forward removes this
   *  exact entry rather than the panel's own UI. */
  pushPanelEntry(key: string, onPop: () => void): void;
  /** In-panel edits that change what's shown without adding a new layer
   *  (panel-system.md's example: switching Assign's add-party form between
   *  an existing party and a new one) — updates the current entry without
   *  growing the stack, so Back still lands one layer back, not here. */
  replacePanelEntry(key: string): void;
  /** UI-driven close (Escape, X, a back arrow): removes the entry `key`
   *  pushed. Only calls `history.back()` when `key` is the topmost entry —
   *  the one browser history can actually rewind past — and marks that
   *  `back()` as ours first, so the `popstate` it causes doesn't also run
   *  `onPop` a second time. A `key` that isn't tracked (already popped, or
   *  never pushed) is a no-op: nothing here calls `history.back()` on its
   *  behalf. */
  popPanelEntry(key: string): void;
  /** Test-only: drops all bookkeeping without touching real history. */
  reset(): void;
}

/**
 * Builds one coordinator instance. `historyImpl`/`windowImpl` are the only
 * seams — everything else is a closure over `entries`, `seqCounter` and
 * `pendingProgrammaticSeq`, so two coordinators (e.g. the real one and a
 * jest fake) never share state.
 */
export function createPanelHistoryCoordinator(
  historyImpl: HistoryLike,
  windowImpl: WindowLike
): PanelHistoryCoordinator {
  let seqCounter = 0;
  const entries: PanelEntry[] = [];
  // Set just before this coordinator's OWN `history.back()` call, to the seq
  // being removed. The popstate that call causes is consumed silently (the
  // entry is already gone — see popPanelEntry) instead of re-running onPop.
  let pendingProgrammaticSeq: number | null = null;
  let listening = false;

  function currentSeq(state: unknown): number {
    return isRediPanelState(state) ? state.seq : -1;
  }

  function handlePopState(event: PopStateLike) {
    if (pendingProgrammaticSeq !== null) {
      // Our own popPanelEntry() already removed the entry and updated
      // whatever UI state goes with it — this popstate is just the browser
      // catching up, not a new external navigation.
      pendingProgrammaticSeq = null;
      return;
    }
    const seq = currentSeq(event.state);
    // LIFO: close every entry newer than wherever history landed, deepest
    // first — a single Back press only ever removes the top one, but a
    // multi-step jump (history.go(-3), a middle-click on Back's dropdown)
    // is handled the same way, one onPop per layer instead of a crash or a
    // silently stale panel.
    while (entries.length > 0 && entries[entries.length - 1].seq > seq) {
      const top = entries.pop();
      top?.onPop();
    }
  }

  function ensureListening() {
    if (listening) return;
    windowImpl.addEventListener('popstate', handlePopState);
    listening = true;
  }

  function pushPanelEntry(key: string, onPop: () => void) {
    ensureListening();
    seqCounter += 1;
    const seq = seqCounter;
    entries.push({ key, seq, onPop });
    historyImpl.pushState({ [STATE_MARK]: true, seq, key } satisfies RediPanelState, '');
  }

  function replacePanelEntry(key: string) {
    const existing = entries.find((e) => e.key === key);
    const seq = existing ? existing.seq : seqCounter;
    historyImpl.replaceState({ [STATE_MARK]: true, seq, key } satisfies RediPanelState, '');
  }

  function popPanelEntry(key: string) {
    const idx = entries.findIndex((e) => e.key === key);
    if (idx === -1) return;
    const isTop = idx === entries.length - 1;
    const [entry] = entries.splice(idx, 1);
    if (!isTop) return; // nothing to rewind — see the doc comment above.
    pendingProgrammaticSeq = entry.seq;
    historyImpl.back();
  }

  function reset() {
    entries.length = 0;
    pendingProgrammaticSeq = null;
    seqCounter = 0;
  }

  return { pushPanelEntry, replacePanelEntry, popPanelEntry, reset };
}

// The app's one coordinator, built from the real browser APIs — lazily, on
// first use rather than at module load, so a jest test that flips
// `Platform.OS` to `'web'` in `beforeAll` (after this module has already
// been imported) still gets a real coordinator wired to jsdom's
// `window.history` instead of freezing in as `null` from whatever
// `Platform.OS` happened to be at import time. Null on native and in
// non-DOM environments — every caller goes through `panelHistory()` so a
// missing coordinator is just a no-op, not a crash.
let cachedPanelHistory: PanelHistoryCoordinator | null | undefined;

export function panelHistory(): PanelHistoryCoordinator | null {
  if (cachedPanelHistory === undefined) {
    cachedPanelHistory =
      Platform.OS === 'web' &&
      typeof window !== 'undefined' &&
      typeof window.history !== 'undefined'
        ? createPanelHistoryCoordinator(window.history, window)
        : null;
  }
  return cachedPanelHistory;
}

/**
 * Opt-in wiring for one panel layer — a CompanionPanel's `companion` slot, a
 * PanelStack's pushed view. Call it every render with whether the layer is
 * currently `active`; it pushes/pops around that transition however it
 * happens (the wrapped closer this returns, or `active` going false through
 * some other code path — e.g. the plan editor closing outright while Assign
 * was still open). `onClose` only ever needs to change component/store
 * state — the history side is entirely this hook's job.
 *
 * `enabled` is the per-surface opt-in panel-system.md calls for: pass
 * `false` (or just don't use this hook) for a phone sheet or any PanelStack
 * that shouldn't touch browser history.
 */
export function usePanelHistoryEntry(
  enabled: boolean,
  active: boolean,
  key: string,
  onClose: () => void
): () => void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const wasActive = useRef(false);

  useEffect(() => {
    const coordinator = enabled ? panelHistory() : null;
    if (!coordinator) return;
    if (active && !wasActive.current) {
      coordinator.pushPanelEntry(key, () => onCloseRef.current());
    } else if (!active && wasActive.current) {
      // Closed some other way — still drop the entry so Back/Forward stay
      // in sync. A no-op if popPanelEntry (below) already removed it.
      coordinator.popPanelEntry(key);
    }
    wasActive.current = active;
    // `key` is stable for the lifetime of one open panel instance at every
    // call site this wires up today (a fixed testID/attachTo string) — a
    // key change mid-open isn't a case any of them hits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, active]);

  return useCallback(() => {
    if (enabled) panelHistory()?.popPanelEntry(key);
    onCloseRef.current();
  }, [enabled, key]);
}

// --- Deep links (panel-system.md's "Deep links on load") -------------------

export interface PanelDeepLinkParams {
  /** `?plan=<id>` */
  planId: string | null;
  /** `&assign=<featureId>` — only meaningful alongside `planId`. */
  assignFeatureId: string | null;
  /** `?post=<postId>` — independent of the plan params, and combinable with
   *  the existing `focusStructureId` channel (useMapFocus.ts). */
  postId: string | null;
}

type RawSearchParams = Record<string, string | string[] | undefined>;

/** Pure by design (no router/store reads) so it's unit-testable on its own —
 *  see `__tests__/hooks/usePanelHistory.test.ts`. `expo-router`'s
 *  `useLocalSearchParams` is the caller's job; this just interprets the
 *  plain object it returns. An array (a repeated query key) takes its first
 *  value, matching how the rest of this codebase's deep-link params behave. */
export function parsePanelDeepLinkParams(params: RawSearchParams): PanelDeepLinkParams {
  const get = (key: string): string | null => {
    const value = params[key];
    if (Array.isArray(value)) return value[0] ?? null;
    return value ?? null;
  };
  const planId = get('plan');
  const assignFeatureId = planId ? get('assign') : null;
  return {
    planId,
    assignFeatureId,
    postId: get('post'),
  };
}
